import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Link,
    TextField,
    Typography
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { Carousel, SocialLoginButtons } from './login';
import WorkInProgress from '../components/popup/work-in-progress';
import InvitationConfirmation from '../components/popup/invitation-confirmation';
import DeletePopup from '../components/popup/delete';
import StepIndicator from '../components/ui/step-indicator';
import VerificationCode from '../components/otp';
import logo from '../files/images/logo.png';
import { useNavigate } from 'react-router';
import { useToast } from '../providers/toaster-provider';

import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import Dropdown from '../components/ui/dropdown';
import NewsletterPopup from '../components/popup/newsletter';
import { useGeolocation } from '../providers/location-provider';

const googleClientId = "863216338324-08ea9cnavjgc7ncliav29d6lanajcasg.apps.googleusercontent.com";
const microsoftClientId = "7cf3faa1-d92c-48aa-a2e0-1e8e304625b7";
const tncVersion = "v1.0.20260116";
const aupVersion = "v1.0.20260116";

const DISABLE_APPLE_OAUTH = true;
const DISABLE_GOOGLE_OAUTH = true;
const DISABLE_MICROSOFT_OAUTH = true;

// PKCE helper functions for Microsoft OAuth
const generateCodeVerifier = (): string => {
    const array = new Uint32Array(56);
    window.crypto.getRandomValues(array);
    return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
};

const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    const bytes = new Uint8Array(digest);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
};

const getCenteredPopupSpecs = (width: number, height: number): string => {
    const left = Math.round((window.screen.width - width) / 2);
    const top = Math.round((window.screen.height - height) / 2);
    
    return [
        `width=${width}`,
        `height=${height}`,
        `left=${left}`,
        `top=${top}`,
        'scrollbars=yes',
        'resizable=yes',
        'toolbar=no',
        'menubar=no',
        'location=no',
        'status=no'
    ].join(',');
};

const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const maskEmail = (email: string) => {
    if (!email || !email.includes('@')) return email;
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return email;
    const visibleChars = Math.max(1, Math.ceil(localPart.length / 3));
    const maskedChars = Math.max(0, localPart.length - visibleChars);
    const maskedLocal = localPart.substring(0, visibleChars) + '*'.repeat(maskedChars);
    return `${maskedLocal}@${domain}`;
};

const Signup: React.FC = () => {
    const { showError, showSuccess } = useToast();
    const navigate = useNavigate();
    const { country, error } = useGeolocation();
    const [showNewsletter, setShowNewsletter] = useState(false);
    const [supportedCountries, setSupportedCountries] = useState<string[]>([]);
    const [countries, setCountries] = useState<{ value: string, label: string }[]>([]);
    const [activeStep, setActiveStep] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [socialLoading, setSocialLoading] = useState<'apple' | 'google' | 'microsoft' | null>(null);
    const [errors, setErrors] = useState<{ email?: string; password?: string; company?: string; country?: string; otp?: string }>({});
    const [otpResendCooldown, setOtpResendCooldown] = useState(0);
    const [form, setForm] = useState({
        email: '',
        password: '',
        agree: false,
        company: '',
        country: ''
    });
    const [socialData, setSocialData] = useState<{
        provider?: string;
        providerId?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
    } | null>(null);
    const [invitationData, setInvitationData] = useState<{
        entityId?: string;
        invitationToken?: string;
        companyName?: string;
        isValid?: boolean;
        isInvitation?: boolean;
        email?: string;
        role?: string;
        countryId?: string;
        countryName?: string;
    } | null>(null);
    const [showInvitationConfirmation, setShowInvitationConfirmation] = useState(false);
    const [showAlreadyRegisteredModal, setShowAlreadyRegisteredModal] = useState(false);
    const [enrollmentData, setEnrollmentData] = useState<{
        token?: string;
        email?: string;
        isValid?: boolean;
    } | null>(null);

    const [register, setRegister] = useApi();
    const [sendOtp, setSendOtp] = useApi();
    const [verifyOtp, setVerifyOtp] = useApi();
    const [checkInvitation, setCheckInvitation] = useApi();
    const [checkEnrollment, setCheckEnrollment] = useApi();
    const [fetchCountries, setFetchCountries] = useApi();
    const [subscribeNewsletter, setSubscribeNewsletter] = useApi();
    const [acceptAgreement, setAcceptAgreement] = useApi();

    // Apple Sign-In configuration and initialization
    useEffect(() => {
        if (typeof window !== 'undefined' && (window as any).AppleID) {
            (window as any).AppleID.auth.init({
                clientId: process.env.REACT_APP_APPLE_CLIENT_ID || 'com.finants.web',
                scope: 'name email',
                redirectURI: window.location.origin,
                state: 'state'
            });
        }
    }, []);

    useEffect(() => {
        document.title = "FineAnts | Sign Up";
        
        // Check for invitation parameters in URL
        const urlParams = new URLSearchParams(window.location.search);
        const entityId = urlParams.get('entityId');
        const invitationToken = urlParams.get('invitationToken');
        const resetToken = urlParams.get('resetToken');

        if (entityId && invitationToken) {
            setInvitationData({ entityId, invitationToken, isInvitation: true });
            setShowInvitationConfirmation(true); // Show overlay immediately while checking

            // Check invitation validity
            const { url, method } = buildApiRequestConfig(routes.userSettings.checkInvitation, [{ param: 'token', value: invitationToken }]);
            setCheckInvitation({
                url,
                method,
            });
        }

        // Check for a password-reset enrollment token (unknown-email forgot-password flow)
        if (resetToken) {
            setEnrollmentData({ token: resetToken });
            const { url, method } = buildApiRequestConfig(routes.user.checkEnrollment, [{ param: 'token', value: resetToken }]);
            setCheckEnrollment({
                url,
                method,
            });
        }

        // Fetch supported countries
        const { url, method } = buildApiRequestConfig(routes.country.fetch);
        setFetchCountries({ url, method });
    }, [setCheckInvitation, setCheckEnrollment, setFetchCountries]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (otpResendCooldown > 0) {
            timer = setTimeout(() => {
                setOtpResendCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [otpResendCooldown]);

    // Handle fetched countries and check if current location is supported
    useEffect(() => {
        const { data, isLoading } = fetchCountries;
        if (data && !isLoading) {
            const countryNames = data.map((c: any) => c.country);
            setSupportedCountries(countryNames);
            
            // Set countries for the dropdown
            setCountries(data.map((c: any) => ({
                label: c.country,
                value: c.id
            })));

            // Auto-select country if supported (and not already selected)
            if (country && countryNames.includes(country)) {
                setForm(prev => {
                    if (!prev.country) {
                        const countryId = data.find((c: any) => c.country === country)?.id;
                        return { ...prev, country: countryId };
                    }
                    return prev;
                });
            }
        }
    }, [fetchCountries, country]);

    useEffect(() => {
        const { data, isLoading, error } = register;
        if (data && !isLoading && data.token && data.user && data.company) {
            showSuccess('Account created successfully. Please login.');
            navigate('/login');
        }
        if (error) {
            showError('Error creating account. Please try again.');
        }
    }, [register, showSuccess, navigate, showError]);

    useEffect(() => {
        const { data, isLoading, error } = subscribeNewsletter;
        if (data && !isLoading) {
            showSuccess('Thank you for subscribing to our newsletter! We will keep you updated.');
            navigate('/login');
        }
        if (error) {
            showError('Error subscribing to newsletter. Please try again.');
        }
    }, [subscribeNewsletter, showSuccess, navigate, showError]);

    useEffect(() => {
        const { data, isLoading, error } = acceptAgreement;
        if (data && !isLoading) {
            // Agreement accepted successfully, move to next step
            setActiveStep(prev => prev + 1);
        }
        if (error) {
            showError('Error accepting agreement. Please try again.');
        }
    }, [acceptAgreement, showError]);

    useEffect(() => {
        const { data, isLoading, error } = sendOtp;
        if (data && !isLoading) {
            // Show success for initial OTP send (step 0) and resend (step 1)
            if (activeStep === 0) {
                showSuccess('Verification code sent successfully.');
                setActiveStep(prev => prev + 1);
            } else if (activeStep === 1) {
                showSuccess('A new verification code has been sent to your email.');
            }
        }
        if (error) {
            let errorMessage = 'Error sending OTP. Please try again.';
            if (typeof error === 'string' && error.toLocaleLowerCase().includes('maximum credits exceeded')) {
                errorMessage = 'Error sending OTP: Maximum credits exceeded. Please contact support.';
            }
            showError(errorMessage);
        }
    }, [sendOtp, showSuccess, showError, activeStep]);

    useEffect(() => {
        const { data, isLoading, error } = verifyOtp;
        if (data && !isLoading) {
            if (data.verified && !data.isRegistered) {
                // Guard: only increment if still on step 1
                setActiveStep(prev => {
                    if (prev === 1) {
                        showSuccess('Email verified successfully!');
                        return prev + 1;
                    }
                    return prev;
                });
                setErrors(prev => ({ ...prev, otp: undefined }));
            } else if (data.verified && data.isRegistered) {
                // User already registered - show modal asking if they want to create a new company instead
                setShowAlreadyRegisteredModal(true);
            } else {
                setErrors(prev => ({ ...prev, otp: 'Invalid verification code' }));
                showError('Invalid verification code. Please try again.');
            }
        }
        if (error) {
            let errorMessage = 'Invalid verification code. Please try again.';
            if (typeof error === 'string') {
                if (error.toLowerCase().includes('expired')) {
                    errorMessage = 'Verification code has expired. Please request a new one.';
                } else if (error.toLowerCase().includes('invalid') || error.toLowerCase().includes('incorrect')) {
                    errorMessage = 'Invalid verification code. Please check and try again.';
                } else if (error.toLowerCase().includes('maximum') || error.toLowerCase().includes('too many')) {
                    errorMessage = 'Too many failed attempts. Please request a new verification code.';
                }
            }
            setErrors(prev => ({ ...prev, otp: errorMessage }));
            showError(errorMessage);
        }
    }, [verifyOtp, showSuccess, showError]);

    useEffect(() => {
        const { data, isLoading, error } = checkInvitation;
        if (data && !isLoading) {
            if (data.entityName && data.invitedEmail && data.isActive) {
                setInvitationData(prev => ({
                    ...prev,
                    isValid: true,
                    companyName: data.entityName,
                    email: data.invitedEmail || prev?.email,
                    role: data.role === 'admin' ? 'Admin' : 'Read Only',
                    countryId: data.countryId ? String(data.countryId) : undefined,
                    countryName: data.countryName || undefined
                }));
                setShowInvitationConfirmation(true);
            } else {
                setInvitationData(prev => ({ ...prev, isValid: false }));
            }
        }
        if (error) {
            setInvitationData(prev => ({ ...prev, isValid: false }));
        }
    }, [checkInvitation, showError]);

    useEffect(() => {
        const { data, isLoading, error } = checkEnrollment;
        if (data && !isLoading) {
            if (data.valid && data.email) {
                setEnrollmentData(prev => ({ ...prev, isValid: true, email: data.email }));
                setForm(prev => ({ ...prev, email: data.email }));
            } else {
                setEnrollmentData(prev => ({ ...prev, isValid: false }));
            }
        }
        if (error) {
            setEnrollmentData(prev => ({ ...prev, isValid: false }));
            showError('This enrollment link is invalid or has expired.');
        }
    }, [checkEnrollment, showError]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleOTPVerification = (otp: string) => {
        setErrors(prev => ({ ...prev, otp: undefined }));
        const { url, method } = buildApiRequestConfig(routes.otp.verify);
        setVerifyOtp({
            url,
            method,
            body: { email: form.email, code: otp }
        });
    };

    const handleResendOTP = () => {
        if (otpResendCooldown > 0) return;
        
        setOtpResendCooldown(60); // 60 seconds cooldown
        setErrors(prev => ({ ...prev, otp: undefined }));
        const { url, method } = buildApiRequestConfig(routes.otp.send);
        setSendOtp({
            url,
            method,
            body: { email: form.email }
        });
    };

    const handleInvitationContinue = () => {
        setShowInvitationConfirmation(false);
        // Prefill email and country from invitation data
        setForm(prev => ({
            ...prev,
            ...(invitationData?.email ? { email: invitationData.email } : {}),
            ...(invitationData?.countryId ? { country: invitationData.countryId } : {})
        }));
    };

    const handleInvitationCancel = () => {
        setShowInvitationConfirmation(false);
        setInvitationData(null);
        // Redirect to login or home page
        navigate('/login');
    };

    const handleClick = () => {
        switch (activeStep) {
            case 0:
                if (!form.email) {
                    setErrors(prev => ({ ...prev, email: 'Email is required' }));
                } else {
                    if (!isValidEmail(form.email)) {
                        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
                    } else {
                        setErrors(prev => ({ ...prev, email: undefined }));
                        
                        // For invitation users and enrollment (password-reset) users, skip OTP verification
                        if (
                            (invitationData?.isInvitation && invitationData?.isValid && invitationData?.email === form.email) ||
                            (enrollmentData?.isValid && enrollmentData?.email === form.email)
                        ) {
                            setActiveStep(2); // Go directly to password step
                        } else {
                            // Normal flow - send OTP
                            setOtpResendCooldown(60); // Start cooldown immediately
                            const { url, method } = buildApiRequestConfig(routes.otp.send);
                            setSendOtp({
                                url,
                                method,
                                body: { email: form.email }
                            });
                        }
                    }
                }
                break;
            case 1:
                // OTP verification step - this is now handled automatically when OTP is entered
                // This button can serve as a manual "Continue" if needed
                showError('Please enter the 6-digit verification code sent to your email.');
                break;
            case 2:
                // Skip password step if using social login
                if (socialData) {
                    setActiveStep(prev => prev + 1);
                } else {
                    if (!form.password) {
                        setErrors(prev => ({ ...prev, password: 'Password is required' }));
                    } else {
                        if (form.password.length < 6) {
                            setErrors(prev => ({ ...prev, password: 'Password must be at least 6 characters' }));
                        } else if (!form.agree) {
                            setErrors(prev => ({ ...prev, password: 'You must accept the terms and conditions' }));
                        } else {
                            setErrors(prev => ({ ...prev, password: undefined }));
                            // Call acceptAgreement endpoint when agree checkbox is ticked
                            // Fetch IP address and then call the API
                            fetch('https://api.ipify.org?format=json')
                                .then(response => response.json())
                                .then(data => {
                                    const { url, method } = buildApiRequestConfig(routes.user.acceptAgreement);
                                    setAcceptAgreement({
                                        url,
                                        method,
                                        body: { 
                                            email: form.email,
                                            ipAddress: data.ip,
                                            tncVersion,
                                            aupVersion
                                        }
                                    });
                                })
                                .catch(err => {
                                    console.error('Error fetching IP address:', err);
                                    showError('Error processing agreement. Please try again.');
                                });
                        }
                    }
                }
                break;
            case 3:
                // Auto-fill company name for invitation users
                const companyName = invitationData?.isInvitation && invitationData?.isValid ? invitationData.companyName : form.company;
                
                if (!companyName || !form.country) {
                    if (!companyName) {
                        setErrors(prev => ({ ...prev, company: 'Company name is required' }));
                    }
                    if (!form.country) {
                        setErrors(prev => ({ ...prev, country: 'Country is required' }));
                    }
                    return;
                }
                
                setErrors(prev => ({ ...prev, company: undefined, country: undefined }));

                const isInvitedSignup = invitationData?.isInvitation && invitationData?.isValid;

                // Invited users are joining an existing, already-active company -
                // the "supported country" gate only applies to new company signups.
                if (!isInvitedSignup) {
                    // Check if selected country is supported
                    const selectedCountry = countries.find(c => c.value === form.country);
                    const isCountrySupported = supportedCountries.length > 0 &&
                        (selectedCountry && supportedCountries.includes(selectedCountry.label));

                    // If country is not supported, show newsletter popup instead of registering
                    if (supportedCountries.length > 0 && !isCountrySupported) {
                        setShowNewsletter(true);
                        return;
                    }
                }
                
                const { url, method } = buildApiRequestConfig(routes.user.register);
                const requestBody: any = {
                    email: form.email,
                    companyName: companyName,
                    countryId: form.country
                };

                // Include invitation token if present
                if (invitationData?.isInvitation && invitationData?.invitationToken) {
                    requestBody.invitationToken = invitationData.invitationToken;
                }

                // Include invitation data if present
                if (invitationData?.isInvitation && invitationData?.entityId && invitationData?.invitationToken) {
                    requestBody.entityId = invitationData.entityId;
                }

                // Include the enrollment token if this is a password-reset enrollment signup
                if (enrollmentData?.isValid && enrollmentData?.token) {
                    requestBody.resetToken = enrollmentData.token;
                }

                // Include social login data if available
                if (socialData) {
                    requestBody.firstName = socialData.firstName;
                    requestBody.lastName = socialData.lastName;
                    requestBody.provider = socialData.provider;
                    requestBody.providerId = socialData.providerId;
                } else {
                    // Regular registration requires password
                    requestBody.password = form.password;
                }

                setRegister({
                    url,
                    method,
                    body: requestBody
                });
                break;
            default:
                setActiveStep(prev => prev + 1);
        }
    };

    const handleAppleSignIn = async () => {
        setSocialLoading('apple');
        if (DISABLE_APPLE_OAUTH) return;
        
        try {
            
            if (typeof window !== 'undefined' && (window as any).AppleID) {
                // Configure Apple Sign-In to use popup
                (window as any).AppleID.auth.init({
                    clientId: process.env.REACT_APP_APPLE_CLIENT_ID || 'com.finants.web',
                    scope: 'name email',
                    redirectURI: window.location.origin,
                    state: 'state',
                    usePopup: true
                });
                
                const response = await (window as any).AppleID.auth.signIn();
                
                console.log('Apple Sign-In Success:', response);
                
                // Handle successful Apple sign-in
                if (response && response.authorization && response.authorization.code) {
                    handleOAuthRegistration('apple', response.authorization.code, response.user);
                } else {
                    setErrors(prev => ({ ...prev, email: 'Apple Sign-In failed. No authorization code received.' }));
                }
                
            } else {
                throw new Error('Apple Sign-In SDK not loaded');
            }
        } catch (error: any) {
            console.error('Apple Sign-In Error:', error);
        } finally {
            setSocialLoading(null);
        }
    };

    const handleGoogleSignIn = () => {
        setSocialLoading('google');
        if (DISABLE_GOOGLE_OAUTH) return;
        
        const clientId = googleClientId;
        const redirectUri = encodeURIComponent(window.location.origin + '/auth/google/callback');
        const scope = encodeURIComponent('openid email profile');
        const responseType = 'code';
        const state = Math.random().toString(36).substring(2, 15);
        
        const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
            `client_id=${clientId}&` +
            `redirect_uri=${redirectUri}&` +
            `response_type=${responseType}&` +
            `scope=${scope}&` +
            `state=${state}`;
        
        // Open popup window (centered)
        const popup = window.open(
            googleAuthUrl,
            'googleSignIn',
            getCenteredPopupSpecs(500, 600)
        );
        
        // Listen for popup messages
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            
            if (event.data.type === 'GOOGLE_SIGN_IN_SUCCESS') {
                handleOAuthRegistration('google', event.data.code);
                popup?.close();
            } else if (event.data.type === 'GOOGLE_SIGN_IN_ERROR') {
                popup?.close();
            }
            
            setSocialLoading(null);
            window.removeEventListener('message', handleMessage);
        };
        
        window.addEventListener('message', handleMessage);
        
        // Handle popup blocked or closed - COOP-safe version
        const checkClosed = setInterval(() => {
            try {
                if (popup?.closed) {
                    clearInterval(checkClosed);
                    setSocialLoading(null);
                    window.removeEventListener('message', handleMessage);
                }
            } catch (error) {
                // COOP policy blocks window.closed access - stop checking
                clearInterval(checkClosed);
            }
        }, 1000);
    };

    const handleMicrosoftSignIn = async () => {
        setSocialLoading('microsoft');
        if (DISABLE_MICROSOFT_OAUTH) return;
        
        try {
            // Generate PKCE parameters
            const codeVerifier = generateCodeVerifier();
            const codeChallenge = await generateCodeChallenge(codeVerifier);
            
            // Store code verifier for later use
            sessionStorage.setItem('pkce_code_verifier', codeVerifier);
            
            // Microsoft OAuth popup configuration with PKCE
            const clientId = microsoftClientId;
            const redirectUri = window.location.origin + '/auth/microsoft/callback';
            const scope = 'openid profile email User.Read';
            const responseType = 'code';
            const state = Math.random().toString(36).substring(2, 15);
            
            const microsoftAuthUrl = `https://login.microsoftonline.com/consumers/oauth2/v2.0/authorize?` +
                `client_id=${encodeURIComponent(clientId)}&` +
                `redirect_uri=${encodeURIComponent(redirectUri)}&` +
                `response_type=${encodeURIComponent(responseType)}&` +
                `scope=${encodeURIComponent(scope)}&` +
                `state=${encodeURIComponent(state)}&` +
                `code_challenge=${encodeURIComponent(codeChallenge)}&` +
                `code_challenge_method=S256&` +
                `response_mode=fragment`;
            
            // Open popup window (centered)
            const popup = window.open(
                microsoftAuthUrl,
                'microsoftSignIn',
                getCenteredPopupSpecs(500, 600)
            );
        
            // Listen for popup messages
            const handleMessage = (event: MessageEvent) => {
                if (event.origin !== window.location.origin) return;
                
                if (event.data.type === 'MICROSOFT_SIGN_IN_SUCCESS') {
                    handleOAuthRegistration('microsoft', event.data.code);
                    popup?.close();
                } else if (event.data.type === 'MICROSOFT_SIGN_IN_ERROR') {
                    popup?.close();
                }
                
                setSocialLoading(null);
                window.removeEventListener('message', handleMessage);
            };
            
            window.addEventListener('message', handleMessage);
            
            // Handle popup blocked or closed - Microsoft COOP-safe version
            const checkClosed = setInterval(() => {
                try {
                    if (popup?.closed) {
                        clearInterval(checkClosed);
                        setSocialLoading(null);
                        window.removeEventListener('message', handleMessage);
                    }
                } catch (error) {
                    // COOP policy blocks window.closed access - stop checking
                    clearInterval(checkClosed);
                    console.log('COOP policy detected, popup monitoring stopped');
                }
            }, 1000);
            
            // Fallback timeout for Microsoft OAuth (5 minutes)
            setTimeout(() => {
                clearInterval(checkClosed);
                setSocialLoading(null);
                window.removeEventListener('message', handleMessage);
                console.log('Microsoft OAuth popup timeout');
            }, 300000);
            
        } catch (error) {
            console.error('Microsoft OAuth PKCE error:', error);
            setSocialLoading(null);
        }
    };

    const handleOAuthRegistration = async (provider: string, code: string, userData?: any) => {
        try {
            setSocialLoading(provider as 'google' | 'microsoft' | 'apple');
            
            const { url, method } = buildApiRequestConfig(routes.user.oauth);
            
            const requestBody: any = {
                provider: provider,
                code: code,
                redirectUri: window.location.origin + `/auth/${provider}/callback`,
                mode: 'registration'
            };
            
            // Add user data for Apple (if provided)
            if (provider === 'apple' && userData) {
                requestBody.user = userData;
            }
            
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });
            
            const data = await response.json();
            
            // Check if social account already exists
            if (data.error === "Social account already exists") {
                setShowAlreadyRegisteredModal(true);
                setSocialLoading(null);
                return;
            }
            
            if (response.ok && data.success && data.profileData) {
                // Autofill registration form with returned profileData
                setSocialData({
                    provider: data.profileData.provider,
                    providerId: data.profileData.providerId,
                    firstName: data.profileData.firstName,
                    lastName: data.profileData.lastName,
                    email: data.profileData.email
                });
                
                // Clear any previous errors
                setErrors({});
                
                // Check if this is an invitation signup with matching email
                if (invitationData?.isInvitation && invitationData?.isValid) {
                    if (invitationData?.email === data.profileData.email) {
                        // Email matches - update form and complete signup immediately
                        setForm(prev => ({
                            ...prev,
                            email: data.profileData.email,
                            agree: true
                        }));
                        
                        // Complete signup immediately for invitation users with matching social account
                        const { url, method } = buildApiRequestConfig(routes.user.register);
                        const requestBody: any = {
                            email: data.profileData.email,
                            companyName: invitationData.companyName,
                            firstName: data.profileData.firstName,
                            lastName: data.profileData.lastName,
                            provider: data.profileData.provider,
                            providerId: data.profileData.providerId,
                            invitationToken: invitationData.invitationToken,
                            entityId: invitationData.entityId
                        };
                        
                        setRegister({
                            url,
                            method,
                            body: requestBody
                        });
                    } else {
                        // Email mismatch - show error and don't update form email
                        showError(`Email mismatch: Please use the account associated with ${invitationData.email}`);
                        setSocialData(null);
                        return;
                    }
                } else {
                    // Normal social signup flow - update form email
                    setForm(prev => ({
                        ...prev,
                        email: data.profileData.email,
                        agree: true
                    }));
                    
                    if (activeStep === 0) {
                        setActiveStep(3); // Skip verification and password, go directly to company step
                    }
                }
                
            } else {
                showError(data.details || data.message || `${provider} Sign-In failed. Please try again.`);
            }
        } catch (error) {
            console.error(`${provider} OAuth Error:`, error);
        } finally {
            setSocialLoading(null);
        }
    };

    const stepRenders = [
        <Username form={form} handleChange={handleChange} errors={errors} invitationData={invitationData} enrollmentData={enrollmentData} />,
        <Verification 
            email={form.email} 
            onVerify={handleOTPVerification}
            onResend={handleResendOTP}
            errors={errors}
            isLoading={verifyOtp.isLoading}
            resendCooldown={otpResendCooldown}
            isResending={sendOtp.isLoading}
        />,
        <Password 
            form={form} 
            errors={errors}
            handleChange={handleChange} 
            showPassword={showPassword} 
            setShowPassword={setShowPassword}
            socialData={socialData} />,
        <Company form={form} handleChange={handleChange} errors={errors} socialData={socialData} invitationData={invitationData} countries={countries} onCountrySelected={(countryId, countryName) => {
            // You can add logic here to check if country is supported
            // For now, we'll just update the state
            setShowNewsletter(false); // Reset in case user changes selection
        }}/>
    ];

    return (
        <Box width={'100vw'} minHeight={'100vh'} bgcolor="var(--color-page-background)">
        <InvitationConfirmation
            open={showInvitationConfirmation}
            onContinue={handleInvitationContinue}
            onCancel={handleInvitationCancel}
            isLoading={checkInvitation.isLoading}
            isValid={invitationData?.isValid}
            invitationData={{
                email: invitationData?.email,
                companyName: invitationData?.companyName,
                role: invitationData?.role
            }}
        />
        <DeletePopup
            open={showAlreadyRegisteredModal}
            message="Account Already Exists"
            description="It looks like you already have an account with us. Would you like to log in and create a new company instead?"
            confirmLabel="Log In & Create Company"
            cancelLabel="Restart Signup"
            onConfirm={() => {
                setShowAlreadyRegisteredModal(false);
                navigate(`/login?createCompany=true&email=${encodeURIComponent(form.email)}`);
            }}
            onCancel={() => {
                setShowAlreadyRegisteredModal(false);
                // Reset to first step
                setActiveStep(0);
                setForm({
                    email: '',
                    password: '',
                    agree: false,
                    company: '',
                    country: ''
                });
                setErrors({});
            }}
        />
        <NewsletterPopup 
            open={showNewsletter}
            onClose={() => {
                setShowNewsletter(false);
                navigate('/login');
            }}
            onSubscribe={async (email) => {
                if (!country) return;
                const { url, method } = buildApiRequestConfig(routes.newsletter.subscribe);
                setSubscribeNewsletter({
                    url,
                    method,
                    body: {
                        email,
                        country
                    }
                });
            }}
            defaultEmail={form.email}
            country={error ? 'Your country' : (country || 'Your country')}
        />
        <WorkInProgress 
            open={socialLoading === 'apple' || socialLoading === 'microsoft' || socialLoading === 'google'} 
            onClose={() => setSocialLoading(null)}/>
        <Box 
            display="flex" 
            minHeight="100vh" 
            bgcolor="var(--color-page-background)" 
            maxWidth={1200} 
            margin="0 auto"
            sx={{
                flexDirection: { xs: 'column', md: 'row' },
                '@media (max-width: 600px)': {
                    minHeight: 'auto',
                    paddingTop: '60px'
                }
            }}
        >
            <Box
                sx={{
                    position: 'absolute', 
                    top: 50,
                    left: 0,
                    boxSizing: 'border-box',
                    '@media (max-width: 600px)': { top: 8, left: 8 },
                }}
                width="100%" 
            >
                <Box maxWidth="1200px" m="0 auto" px={{ xs: '16px', sm: '64px' }} boxSizing="border-box" display="flex" justifyContent="flex-end">
                    <img src={logo} alt="FineAnts" style={{ height: 45 }} />
                </Box>
            </Box>
            <Box 
                flex={1} 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                sx={{
                    display: { xs: 'none', md: 'flex' }
                }}
            >
                <Carousel />
            </Box>
            <Box 
                flex={1} 
                display="flex" 
                flexDirection="column" 
                justifyContent={{ md: "center", xs: 'flex-start' }} 
                px={{ xs: 3, sm: 8 }}
                py={{ xs: 3, md: 0 }}
                minHeight={{ xs: 'calc(100vh - 60px)', md: '100vh' }}
            >
                <Box width="100%" maxWidth={480}>
                    <StepIndicator
                        steps={invitationData?.isInvitation && invitationData?.isValid ? 
                            ['Setup', 'Password', 'Company'] : 
                            ['Setup', 'Verification', 'Password', 'Company']
                        }
                        activeStep={invitationData?.isInvitation && invitationData?.isValid && activeStep > 1 ? activeStep - 1 : activeStep}
                    />
                    {stepRenders[activeStep]}
                    <Button
                        variant="contained"
                        fullWidth
                        disabled={
                            (activeStep === 0 && (sendOtp.isLoading || !form.email || !isValidEmail(form.email))) ||
                            (activeStep === 1 && (verifyOtp.isLoading || !form.email)) ||
                            (activeStep === 2 && (form.password.length < 6 || !form.agree)) ||
                            (activeStep === 3 && (register.isLoading || !form.country || (!(invitationData?.isInvitation && invitationData?.isValid) && !form.company)))
                        }
                        onClick={handleClick}
                        sx={{ 
                            bgcolor: 'var(--color-primary)', 
                            color: '#fff', 
                            fontWeight: 600, 
                            py: 1.2, 
                            borderRadius: 1, 
                            boxShadow: 'none', 
                            mb: 1, 
                            mt: 1,
                            display: activeStep === 1 ? 'none' : 'block'
                        }}
                    >
                        {activeStep === 0 ? (sendOtp.isLoading ? 'Sending OTP...' : 'Next') :
                         activeStep === 1 ? (verifyOtp.isLoading ? 'Verifying...' : 'Continue') :
                         activeStep === 3 ? (register.isLoading ? (invitationData?.isInvitation ? 'Completing...' : 'Creating...') : (invitationData?.isInvitation ? 'Complete Registration' : 'Create')) : 'Next'}
                    </Button>
                    {activeStep === 0 && (
                        <Typography align="center" fontSize={14} color="text.secondary" mb={2}>
                            Already have an account? <span style={{ color: '#FF8682', cursor: 'pointer' }}onClick={()=> navigate('/login')}>Login</span>
                        </Typography>
                    )}
                    { activeStep === 0 && 
                        <>
                            <Divider sx={{ my: 2, color: '#B3B3B3', fontSize: 14 }}>Or sign up with</Divider>
                            <SocialLoginButtons 
                                onAppleClick={handleAppleSignIn}
                                onGoogleClick={handleGoogleSignIn}
                                onMicrosoftClick={handleMicrosoftSignIn}
                                loading={socialLoading}
                            />
                        </>
                    }
                </Box>
            </Box>
        </Box>
        </Box>
    );
};

type UsernameProps = {
    form: any;
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    errors: { email?: string; password?: string; company?: string; country?: string; otp?: string };
}

const Username: React.FC<UsernameProps & { invitationData?: any; enrollmentData?: any }> = ({ form, handleChange, errors, invitationData, enrollmentData }) => {
    const isInvitationUser = invitationData?.isInvitation && invitationData?.isValid && invitationData?.email;
    const isEnrollmentUser = enrollmentData?.isValid && enrollmentData?.email;
    const isEmailLocked = isInvitationUser || isEnrollmentUser;

    return (
        <Box mt={2}>
            <Typography variant="h4" fontWeight={600} mb={1}>
                Please set up your account
            </Typography>
            <Typography color="text.secondary" mb={3}>
                {invitationData?.isInvitation && invitationData?.isValid
                    ? "You've been invited to join this company account. Please confirm to complete your registration."
                    : isEnrollmentUser
                        ? "Let's get your account set up so you can log in."
                        : "Let's get you all set up so you can access your personal account."}
            </Typography>
            <TextField
                label="Email"
                name="email"
                fullWidth
                margin="dense"
                value={form.email}
                error={!!errors.email}
                helperText={errors.email || (isEmailLocked ? 'This email is connected to your invitation and cannot be changed.' : '')}
                onChange={handleChange}
                disabled={isEmailLocked}
                sx={{
                    '& .MuiInputBase-input': {
                        backgroundColor: isEmailLocked ? 'var(--color-container-background)' : 'white'
                    }
                }}
            />
        </Box>
    );
};

type VerificationProps = {
    email: string;
    onVerify: (otp: string) => void;
    onResend: () => void;
    errors: { email?: string; password?: string; company?: string; country?: string; otp?: string };
    isLoading?: boolean;
    resendCooldown: number;
    isResending?: boolean;
}

const Verification: React.FC<VerificationProps> = ({ 
    email, 
    onVerify, 
    onResend, 
    errors, 
    isLoading, 
    resendCooldown,
    isResending 
}) => {
    const [otp, setOtp] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);

    // Auto-verify when OTP is complete
    useEffect(() => {
        if (otp.length === 6 && !hasSubmitted && !isLoading) {
            setHasSubmitted(true);
            onVerify(otp);
        }
    }, [otp, onVerify, hasSubmitted, isLoading]);

    // Reset submission state when OTP changes
    useEffect(() => {
        if (otp.length < 6) {
            setHasSubmitted(false);
        }
    }, [otp]);

    // Reset OTP when there's an error
    useEffect(() => {
        if (errors.otp) {
            setOtp('');
            setHasSubmitted(false);
        }
    }, [errors.otp]);

    const handleOTPComplete = (completedOtp: string) => {
        if (!hasSubmitted && !isLoading) {
            setHasSubmitted(true);
            onVerify(completedOtp);
        }
    };

    return (
        <Box my={2}>
            <Typography variant="h4" fontWeight={600} mb={1}>
                Please enter verification code
            </Typography>
            <Typography color="text.secondary" mb={3}>
                The verification code has been sent to {maskEmail(email)}
            </Typography>
            <VerificationCode 
                onChange={setOtp}
                onComplete={handleOTPComplete}
                isError={!!errors.otp}
                clearAttempts={resendCooldown === 60}
            />
            {errors.otp && (
                <Typography color="error" fontSize={12} textAlign="center" mt={1}>
                    {errors.otp}
                </Typography>
            )}
            {isLoading && (
                <Typography color="primary" fontSize={12} textAlign="center" mt={1}>
                    Verifying code...
                </Typography>
            )}
            <Box mt={3} display="flex" justifyContent="center" alignItems="center" gap={1}>
                <Typography color="text.secondary" fontSize={14}>
                    Didn't receive the code?
                </Typography>
                <Button
                    variant="text"
                    onClick={onResend}
                    disabled={resendCooldown > 0 || isResending}
                    sx={{ 
                        color: 'var(--color-primary)', 
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: 14,
                        p: 0,
                        minWidth: 'auto'
                    }}
                >
                    {isResending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
                </Button>
            </Box>
        </Box>
    );
};

type PasswordProps = {
    form: any;
    handleChange: (e: React.ChangeEvent<HTMLInputElement>)=> void;
    showPassword: boolean;
    setShowPassword: React.Dispatch<React.SetStateAction<boolean>>;
    errors: { email?: string; password?: string; company?: string; country?: string; otp?: string };
    socialData?: { provider?: string; providerId?: string; firstName?: string; lastName?: string; email?: string; } | null;
}

const Password: React.FC<PasswordProps> = ({ form, handleChange, showPassword, setShowPassword, errors, socialData }) => {
    // Skip password step if using social login
    if (socialData) {
        return (
            <Box my={2}>
                <Typography variant="h4" fontWeight={600} mb={1}>
                    Social Login Detected
                </Typography>
                <Typography color="text.secondary" mb={3}>
                    You're signing up with {socialData.provider}. No password required!
                </Typography>
                <Box sx={{ 
                    p: 2, 
                    backgroundColor: '#e8f5e8', 
                    borderRadius: 2, 
                    border: '1px solid #4caf50'
                }}>
                    <Typography variant="body2" color="primary" fontWeight="medium">
                        ✓ Connected with {socialData.provider} ({socialData.email})
                    </Typography>
                </Box>
            </Box>
        );
    }
    return (
        <Box my={2}>
            <Typography variant="h4" fontWeight={600} mb={1}>
            Please create your password
            </Typography>
            <Typography color="text.secondary" mb={3}>
            Please create a unique password to proceed 
            </Typography>
            <TextField
                label="Create Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                margin="dense"
                value={form.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                    endAdornment: (
                    <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(s => !s)} edge="end" tabIndex={-1}>
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                    </InputAdornment>
                    ),
                    style: { background: 'var(--color-page-background)' },
                }}
                sx={{
                    '& input::-ms-reveal, & input::-ms-clear': { display: 'none' },
                    '& input::-webkit-contacts-auto-fill-button, & input::-webkit-credentials-auto-fill-button': { display: 'none' },
                }}
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={form.agree}
                        onChange={handleChange}
                        name="agree"
                    />
                }
                label={<span style={{ fontSize: 14 }}>I agree to all the <Link href="/terms-and-conditions?tab=0" target="_blank" sx={{ color: '#FF8682', cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Terms & Conditions</Link> and <Link href="/terms-and-conditions?tab=1" target="_blank" sx={{ color: '#FF8682', cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>AUP</Link></span>}
                sx={{ mt: 2, mb: 2 }}
            />
        </Box>
    );
};

type CompanyProps = {
    form: any;
    handleChange: (e: React.ChangeEvent<HTMLInputElement>)=> void;
    errors: { email?: string; password?: string; company?: string; country?: string; otp?: string };
    socialData?: { provider?: string; providerId?: string; firstName?: string; lastName?: string; email?: string; } | null;
    invitationData?: { entityId?: string; invitationToken?: string; companyName?: string; isValid?: boolean; isInvitation?: boolean; email?: string; role?: string; countryId?: string; countryName?: string; } | null;
    onCountrySelected?: (countryId: string, countryName: string) => void;
    countries?: { value: string, label: string }[];
}

const Company: React.FC<CompanyProps> = ({ form, handleChange, errors, socialData, invitationData, onCountrySelected, countries = [] }) => {

    return (
        <Box my={2}>
            <Typography variant="h4" fontWeight={600} mb={1}>
                {invitationData?.isInvitation && invitationData?.isValid ? 'Confirm Your Company Details' : 'Add your Company Details'}
            </Typography>
            <Typography color="text.secondary" mb={3}>
                Let’s get you all set up so you can access your personal account.
            </Typography>
            {socialData && (
                <Box sx={{ 
                    p: 2, 
                    mb: 2,
                    backgroundColor: '#e8f5e8', 
                    borderRadius: 2, 
                    border: '1px solid #4caf50'
                }}>
                    <Typography variant="body2" color="primary" fontWeight="medium">
                        ✓ Connected with {socialData.provider} ({socialData.email})
                    </Typography>
                </Box>
            )}
            <Box display="flex" justifyContent="space-between" gap={2}>
                <TextField
                    label="Company Name"
                    required
                    name="company"
                    fullWidth
                    value={invitationData?.isInvitation && invitationData?.isValid ? invitationData.companyName : form.company}
                    onChange={handleChange}
                    error={!!errors.company}
                    helperText={errors.company || (invitationData?.isInvitation && invitationData?.isValid ? 'Company name is pre-filled from your invitation' : '')}
                    disabled={invitationData?.isInvitation && invitationData?.isValid}
                    sx={{
                        '& .MuiInputBase-input': {
                            backgroundColor: invitationData?.isInvitation && invitationData?.isValid ? 'var(--color-container-background)' : 'white'
                        }
                    }}
                />
                {invitationData?.isInvitation && invitationData?.isValid && invitationData?.countryId ? (
                    <TextField
                        label="Country"
                        fullWidth
                        value={invitationData.countryName || ''}
                        disabled
                        helperText="Country pre-filled from your company's settings"
                        sx={{
                            '& .MuiInputBase-input': {
                                backgroundColor: 'var(--color-container-background)'
                            }
                        }}
                    />
                ) : (
                    <Dropdown
                        value={form.country}
                        options={countries}
                        height={55}
                        title="Country"
                        placeholder="Select Country"
                        error={!!errors.country}
                        helperText={errors.country || ''}
                        onChange={(value: string) => {
                            const selectedCountry = countries.find(c => c.value === value);
                            if (onCountrySelected && selectedCountry) {
                                onCountrySelected(value, selectedCountry.label);
                            }
                            handleChange({
                                target: {
                                    name: 'country',
                                    value,
                                    type: 'text',
                                    checked: false
                                } as React.ChangeEvent<HTMLInputElement>['target']
                            } as React.ChangeEvent<HTMLInputElement>)
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

export default Signup;
